import type { LetterPickerErrorData } from '../components/learning/LetterPicker';

export interface ProsodyError {
  type: 'weak_form' | 'contraction' | 'elision' | 'linking' | 'assimilation';
  word: string;
  position: number; // word index in sentence
}

/** Common English function words that have weak forms in connected speech */
const WEAK_FORM_WORDS = new Set([
  'a', 'an', 'the', 'to', 'of', 'for', 'and', 'or', 'but',
  'can', 'has', 'have', 'had', 'was', 'were', 'is', 'are',
  'do', 'does', 'did', 'am', 'be', 'been',
  'he', 'she', 'we', 'you', 'them', 'him', 'her', 'us',
  'his', 'your', 'my', 'our', 'their',
  'at', 'from', 'in', 'on', 'with', 'as', 'that', 'than',
  'some', 'would', 'could', 'should', 'will', 'shall', 'must',
]);

/** Vowel letters — errors on these in weak-form words suggest listener couldn't hear reduced vowel */
const VOWELS = new Set(['a', 'e', 'i', 'o', 'u']);

/** Build a map from character position to word index and word string */
function buildPositionMap(text: string): Array<{ wordIndex: number; word: string; posInWord: number }> {
  const result: Array<{ wordIndex: number; word: string; posInWord: number }> = [];
  const words = text.split(/\s+/);
  let charPos = 0;

  for (let wi = 0; wi < words.length; wi++) {
    const word = words[wi];
    for (let ci = 0; ci < word.length; ci++) {
      result[charPos] = { wordIndex: wi, word, posInWord: ci };
      charPos++;
    }
    // Account for the space
    if (wi < words.length - 1) {
      result[charPos] = { wordIndex: wi, word: ' ', posInWord: 0 };
      charPos++;
    }
  }

  return result;
}

/** Check if position is at/near an apostrophe in a contraction */
function isContractionError(word: string, posInWord: number): boolean {
  const lower = word.toLowerCase();
  // Common contractions: don't, can't, won't, isn't, aren't, wasn't, weren't,
  // he's, she's, it's, I'm, you're, we're, they're, I've, you've, we've,
  // I'll, you'll, he'll, she'll, they'll, I'd, you'd, he'd, she'd, they'd
  const apostropheIdx = lower.indexOf("'");
  if (apostropheIdx === -1) return false;

  // Error is near the apostrophe (within 1 char)
  return Math.abs(posInWord - apostropheIdx) <= 1;
}

/** Check if error position is at a known elision site (t/d between consonants) */
function isElisionError(word: string, posInWord: number, nextWord: string | undefined): boolean {
  const lower = word.toLowerCase();
  const char = lower[posInWord];
  if (char !== 't' && char !== 'd') return false;

  // Elision at word-final position: last/next, hand/bag, must/be
  if (posInWord === lower.length - 1 && nextWord) {
    const prevChar = posInWord > 0 ? lower[posInWord - 1] : '';
    const nextChar = nextWord[0]?.toLowerCase() || '';
    // t/d between consonants (prev is consonant, next word starts with consonant)
    if (prevChar && !VOWELS.has(prevChar) && nextChar && !VOWELS.has(nextChar)) {
      return true;
    }
  }

  // Mid-word elision patterns: -stl- (castle), -ftn- (often)
  if (posInWord > 0 && posInWord < lower.length - 1) {
    const prev = lower[posInWord - 1];
    const next = lower[posInWord + 1];
    if (!VOWELS.has(prev) && !VOWELS.has(next)) {
      return true;
    }
  }

  return false;
}

/** Check if error is at a word boundary where linking occurs (C+V) */
function isLinkingError(
  word: string,
  posInWord: number,
  wordIndex: number,
  words: string[],
): boolean {
  // Linking happens at word boundaries: consonant end + vowel start
  const lower = word.toLowerCase();

  // Error at last char of word, and next word starts with vowel
  if (posInWord === lower.length - 1 && wordIndex < words.length - 1) {
    const lastChar = lower[posInWord];
    const nextFirstChar = words[wordIndex + 1]?.[0]?.toLowerCase();
    if (lastChar && !VOWELS.has(lastChar) && nextFirstChar && VOWELS.has(nextFirstChar)) {
      return true;
    }
  }

  // Error at first char of word, and prev word ends with consonant
  if (posInWord === 0 && wordIndex > 0) {
    const firstChar = lower[0];
    const prevWord = words[wordIndex - 1];
    const prevLastChar = prevWord?.[prevWord.length - 1]?.toLowerCase();
    if (firstChar && VOWELS.has(firstChar) && prevLastChar && !VOWELS.has(prevLastChar)) {
      return true;
    }
  }

  return false;
}

/** Check if error is at an assimilation boundary (t+y, d+y, s+y) */
function isAssimilationError(
  word: string,
  posInWord: number,
  wordIndex: number,
  words: string[],
): boolean {
  const lower = word.toLowerCase();

  // Assimilation at word boundary: current word ends with t/d/s, next starts with y
  if (posInWord === lower.length - 1 && wordIndex < words.length - 1) {
    const lastChar = lower[posInWord];
    const nextFirstChar = words[wordIndex + 1]?.[0]?.toLowerCase();
    if ((lastChar === 't' || lastChar === 'd' || lastChar === 's') && nextFirstChar === 'y') {
      return true;
    }
  }

  // Next word starts with 'y' and error is on the first char of that word
  if (posInWord === 0 && lower[0] === 'y' && wordIndex > 0) {
    const prevWord = words[wordIndex - 1];
    const prevLastChar = prevWord?.[prevWord.length - 1]?.toLowerCase();
    if (prevLastChar === 't' || prevLastChar === 'd' || prevLastChar === 's') {
      return true;
    }
  }

  return false;
}

/**
 * Detect prosody-related listening errors from LetterPicker error data.
 * These are errors likely caused by connected speech phenomena
 * (weak forms, contractions, elision, linking, assimilation)
 * rather than simple letter confusion.
 */
export function detectProsodyErrors(
  expectedText: string,
  errorData: LetterPickerErrorData,
): ProsodyError[] {
  if (errorData.errors.length === 0) return [];

  const posMap = buildPositionMap(expectedText);
  const words = expectedText.split(/\s+/);
  const results: ProsodyError[] = [];
  const seen = new Set<string>(); // dedupe by type+word

  for (const err of errorData.errors) {
    const info = posMap[err.position];
    if (!info || info.word === ' ') continue;

    const wordLower = info.word.toLowerCase().replace(/[^a-z']/g, '');
    const expectedChar = err.expected.toLowerCase();
    const nextWord = info.wordIndex < words.length - 1 ? words[info.wordIndex + 1] : undefined;

    // Priority order: contraction > weak_form > assimilation > elision > linking

    // 1. Contraction errors
    if (isContractionError(info.word, info.posInWord)) {
      const key = `contraction:${wordLower}`;
      if (!seen.has(key)) {
        seen.add(key);
        results.push({ type: 'contraction', word: info.word, position: info.wordIndex });
      }
      continue;
    }

    // 2. Weak form errors (error on vowel in a function word)
    if (WEAK_FORM_WORDS.has(wordLower) && VOWELS.has(expectedChar)) {
      const key = `weak_form:${wordLower}`;
      if (!seen.has(key)) {
        seen.add(key);
        results.push({ type: 'weak_form', word: info.word, position: info.wordIndex });
      }
      continue;
    }

    // 3. Assimilation errors
    if (isAssimilationError(info.word, info.posInWord, info.wordIndex, words)) {
      const key = `assimilation:${wordLower}`;
      if (!seen.has(key)) {
        seen.add(key);
        results.push({ type: 'assimilation', word: info.word, position: info.wordIndex });
      }
      continue;
    }

    // 4. Elision errors (t/d dropped between consonants)
    if (isElisionError(info.word, info.posInWord, nextWord)) {
      const key = `elision:${wordLower}`;
      if (!seen.has(key)) {
        seen.add(key);
        results.push({ type: 'elision', word: info.word, position: info.wordIndex });
      }
      continue;
    }

    // 5. Linking errors (word boundary confusion)
    if (isLinkingError(info.word, info.posInWord, info.wordIndex, words)) {
      const key = `linking:${wordLower}`;
      if (!seen.has(key)) {
        seen.add(key);
        results.push({ type: 'linking', word: info.word, position: info.wordIndex });
      }
    }
  }

  return results;
}
